/*
 * ET - Bundle Builder (sections/et-bundle-builder.liquid)
 * <et-bundle-builder> owns one bundle state and renders both surfaces from it: the product grid on the left (the
 * "Add to Bundle" / "Remove" button of every card) and the "Your Bundle" panel on the right (line items, quantity
 * steppers, totals, savings badge, discount threshold bar). Nothing reloads the page.
 *
 * Markup contract (all hooks are data attributes, never classes):
 *   root            <et-bundle-builder data-add-label data-remove-label data-count-label data-count-label-one
 *                     data-items-label data-items-label-one data-save-label data-threshold-label
 *                     data-threshold-reached-label data-threshold-min-label data-min-items data-property-key
 *                     data-property-value data-bundle-id-key data-max-quantity data-sticky-panel>
 *   tiers           [data-et-bb-tiers]                JSON [{minimum (cents), percentage, label}]
 *   tabs            [data-et-bb-tabs] > [data-et-bb-tab="<block id>"]
 *   grid            [data-et-bb-grid] > [data-et-bb-card][data-et-bb-product|variant|price|compare|title|
 *                     variant-title|date|order|available|tabs]
 *   card button     [data-et-bb-toggle] > [data-et-bb-toggle-label]
 *   toolbar         [data-et-bb-count] · [data-et-bb-sort] · [data-et-bb-sort-value]
 *   panel           [data-et-bb-panel] · [data-et-bb-selected] + [data-et-bb-panel-title] (twice: desktop +
 *                     mobile heading)
 *                   [data-et-bb-items] + [data-et-bb-item-template] · [data-et-bb-empty]
 *                   [data-et-bb-total] · [data-et-bb-savings] · [data-et-bb-threshold] +
 *                   [data-et-bb-threshold-message] · [data-et-bb-submit] · [data-et-bb-error]
 *                   row: [data-et-bb-item-title|variant|discounts|compare|price] · [data-et-bb-qty]
 *   stepper         [data-et-bb-step="-1"|"1"], each with data-label-remove / -decrease / -increase: [title]
 *                   templates filled in by updateItem, never a string concatenated in JavaScript
 *   mobile scroller [data-et-bb-scrollbar] > [data-et-bb-scrollbar-thumb]
 *
 * Filtering is client side: every product has exactly one DOM card, carrying the ids of the tabs it belongs to, so a
 * tab only toggles `hidden`. Sorting reorders the same cards (the cards Liquid rendered, never the collection).
 * The threshold sentence is a copy setting with [amount] / [percent] value tokens and a [hi] ... [/hi] run marker,
 * so the Figma's coral "7% savings" stays one highlighted run in any language.
 * Money is handled in cents and formatted with the shop's own money format, so JS totals read exactly like the
 * Liquid-rendered prices next to them (never Intl.NumberFormat, which sets "€ 119,95" with a space on a Dutch EUR
 * shop).
 *
 * Prices are Shopify's own, 1:1 with the cart drawer. Which discount a line gets is decided by Shopify, not by the
 * theme: the bundle discount (Finex Discount App) does not combine with other product discounts, so a "Free Smart
 * Plug" from the SMART Discounts app can replace it on every line, a discount code can join in, and the tier pools
 * every _bundle_page line already in the cart. So after every change the panel prices a throwaway copy of the
 * shopper's cart plus the bundle through the tokenless Storefront API (cartCreate on the myshopify.com domain,
 * cross-origin and without cookies - the shopper's real cart and session are never touched, see fetchPricing)
 * and shows exactly what the drawer will: per line the struck price, the discounted price and the discount titles,
 * and the bundle total. Until that answer lands (about 250ms debounce + one request) the panel shows the local
 * estimate from the tier blocks, and it keeps it if the request fails.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-bundle-builder')) return;

  var SECTIONS_PER_REQUEST = 5;
  // Tokenless Storefront API (no access token needed, carts are in scope); any supported version works.
  var STOREFRONT_API_VERSION = '2026-04';
  // One price check per pause, not one per +/- press.
  var PRICING_DELAY = 250;
  var PRICING_SELECTION =
    'cartCreate(input: $input) { cart {' +
    ' discountAllocations { discountedAmount { amount } }' +
    ' lines(first: 100) { nodes { quantity attributes { key value } merchandise { ... on ProductVariant { id } }' +
    ' cost { amountPerQuantity { amount } compareAtAmountPerQuantity { amount } totalAmount { amount } }' +
    ' discountAllocations { discountedAmount { amount }' +
    ' ... on CartAutomaticDiscountAllocation { title } ... on CartCodeDiscountAllocation { code }' +
    ' ... on CartCustomDiscountAllocation { title } } } } } userErrors { message } }';
  var MOBILE_QUERY = '(max-width: 749px)';
  // The band where the panel sits beside the grid, so it is the only one where sticky means anything: the reflow
  // ledger in the header of assets/et-bundle-builder.css derives the 1129px.
  var TWO_COLUMN_QUERY = '(min-width: 1129px)';

  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------ money */

  function delimited(cents, precision, thousands, decimal) {
    var negative = cents < 0;
    var fixed = (Math.abs(cents) / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
    return (negative ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
  }

  /**
   * Shopify's own money placeholders, so the output is byte-identical to the Liquid `money` filter.
   * `presentment` marks an amount that is already in the shopper's currency (a Storefront API price), so the market
   * rate that converts the shop-currency prices of the Liquid is not applied a second time.
   */
  function formatMoney(cents, noDecimals, presentment) {
    var settings = (window.themeVariables && window.themeVariables.settings) || {};
    var format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    if (!format) format = '€{{amount_with_comma_separator}}';

    var rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    if (presentment || !isFinite(rate) || rate <= 0) rate = 1;
    var value = Math.round(cents * rate);

    var output = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount':
          return delimited(value, noDecimals ? 0 : 2, ',', '.');
        case 'amount_no_decimals':
          return delimited(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return delimited(value, noDecimals ? 0 : 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return delimited(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return delimited(value, noDecimals ? 0 : 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return delimited(value, 0, ' ', ',');
        case 'amount_with_space_separator':
          return delimited(value, noDecimals ? 0 : 2, ' ', ',');
        default:
          return match;
      }
    });

    // A money format may carry markup (<span class="money">); the values are written with textContent.
    return output.replace(/<[^>]*>/g, '').trim();
  }

  /* ------------------------------------------------------------------ helpers */

  function toInt(value, fallback) {
    var number = parseInt(value, 10);
    return isFinite(number) ? number : fallback;
  }

  function matches(query) {
    return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  }

  function setText(element, value) {
    if (element) element.textContent = value;
  }

  function fill(template, token, value) {
    if (!template) return '';
    return template.split(token).join(value);
  }

  /** A Storefront API MoneyV2 in cents. */
  function cents(money) {
    var value = parseFloat(money && money.amount);
    return isFinite(value) ? Math.round(value * 100) : 0;
  }

  /** Line item properties / cart attributes as Storefront API attributes; an empty value is not a valid attribute. */
  function toAttributes(object) {
    var attributes = [];
    Object.keys(object || {}).forEach(function (key) {
      var value = object[key];
      if (value === null || value === undefined || value === '') return;
      attributes.push({ key: key, value: typeof value === 'string' ? value : JSON.stringify(value) });
    });
    return attributes;
  }

  /** Every wanted attribute is on the line with the same value (the cart may carry more). */
  function hasAttributes(line, wanted) {
    var present = {};
    (line.attributes || []).forEach(function (attribute) {
      present[attribute.key] = attribute.value;
    });
    return wanted.every(function (attribute) {
      return present[attribute.key] === attribute.value;
    });
  }

  class EtBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.items = {};
      this.order = [];
      this.rows = {};
      this.busy = false;
      // The bundle that has already been POSTed, as a signature (see bundleSignature). Null until the first
      // successful add. It is never reset - it simply stops matching as soon as the shopper changes a product or a
      // quantity, so only a bundle that is ALREADY in the cart, unchanged, keeps the CTA locked.
      this.orderedSignature = null;
      // Shopify's prices per pricing key (see pricingKey): { pending: true } while the request runs, then the priced
      // view, or { failed: true } so a broken request is not retried in a loop.
      this.pricing = {};
      // The shopper's cart (cart.js), priced together with the bundle; null until the first price check needs it.
      this.cart = null;

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.init.bind(this), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onCartChange) {
        document.documentElement.removeEventListener('cart:change', this.onCartChange);
        document.documentElement.removeEventListener('cart:refresh', this.onCartChange);
      }
      window.clearTimeout(this.pricingTimer);
    }

    /* ---------------------------------------------------------------- setup */

    init() {
      this.grid = this.querySelector('[data-et-bb-grid]');
      this.cards = this.grid ? Array.prototype.slice.call(this.grid.querySelectorAll('[data-et-bb-card]')) : [];
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-et-bb-tab]'));
      this.countEl = this.querySelector('[data-et-bb-count]');
      this.sortSelect = this.querySelector('[data-et-bb-sort]');
      this.sortValue = this.querySelector('[data-et-bb-sort-value]');
      this.panel = this.querySelector('[data-et-bb-panel]');
      this.itemsEl = this.querySelector('[data-et-bb-items]');
      this.itemTemplate = this.querySelector('[data-et-bb-item-template]');
      this.emptyEl = this.querySelector('[data-et-bb-empty]');
      this.totalEl = this.querySelector('[data-et-bb-total]');
      this.savingsEl = this.querySelector('[data-et-bb-savings]');
      this.thresholdEl = this.querySelector('[data-et-bb-threshold]');
      this.thresholdMessage = this.querySelector('[data-et-bb-threshold-message]');
      this.submitEl = this.querySelector('[data-et-bb-submit]');
      this.errorEl = this.querySelector('[data-et-bb-error]');
      this.scrollbar = this.querySelector('[data-et-bb-scrollbar]');
      this.scrollbarThumb = this.querySelector('[data-et-bb-scrollbar-thumb]');
      this.selectedEls = Array.prototype.slice.call(this.querySelectorAll('[data-et-bb-selected]'));
      this.panelTitles = Array.prototype.slice.call(this.querySelectorAll('[data-et-bb-panel-title]'));

      this.maxQuantity = Math.max(1, toInt(this.dataset.maxQuantity, 10));
      this.minItems = Math.max(1, toInt(this.dataset.minItems, 1));
      this.tiers = this.readTiers();

      this.addEventListener('click', this.onClick.bind(this));
      if (this.sortSelect) this.sortSelect.addEventListener('change', this.onSort.bind(this));
      if (this.grid) this.grid.addEventListener('scroll', this.updateScrollbar.bind(this), { passive: true });

      this.onResize = this.onResizeHandler.bind(this);
      window.addEventListener('resize', this.onResize);

      // A change in the drawer (a quantity, a removed line, a code) changes what the bundle costs next to it.
      this.onCartChange = this.onCartChangeHandler.bind(this);
      document.documentElement.addEventListener('cart:change', this.onCartChange);
      document.documentElement.addEventListener('cart:refresh', this.onCartChange);

      this.initSticky();
      this.syncTabLinks();
      this.applyTabFromUrl();
      this.applyFilter();
      this.render();
    }

    readTiers() {
      var holder = this.querySelector('[data-et-bb-tiers]');
      if (!holder) return [];
      var parsed = [];
      try {
        parsed = JSON.parse(holder.textContent || '[]');
      } catch (error) {
        parsed = [];
      }
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map(function (tier) {
          return {
            minimum: toInt(tier && tier.minimum, 0),
            percentage: parseFloat((tier && tier.percentage) || 0) || 0,
            label: (tier && tier.label) || ''
          };
        })
        .filter(function (tier) {
          return tier.minimum > 0 && tier.percentage > 0;
        })
        .sort(function (a, b) {
          return a.minimum - b.minimum;
        });
    }

    /* ---------------------------------------------------------------- tabs, filtering, sorting */

    /*
     * The Liquid can only write `{{ request.path }}?bundle_category=<id>`: Shopify Liquid has no access to the query
     * string, so the server-rendered href silently DROPS every other parameter the shopper arrived with (?view=,
     * ?variant=, the utm_* set, a preview token). A plain click never uses the href - it is intercepted - but
     * cmd/ctrl/shift-click deliberately is not, and it must open the page the shopper is actually on, only filtered.
     * Rebuilt from the live address here, replacing our own parameter and nothing else.
     */
    syncTabLinks() {
      this.tabs.forEach(function (tab) {
        var id = tab.dataset.etBbTab;
        if (!id) return;
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('bundle_category', id);
          tab.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (error) {
          /* A browser without URL support keeps the server-rendered href. */
        }
      });
    }

    applyTabFromUrl() {
      var match = window.location.search.match(/[?&]bundle_category=([^&]+)/);
      if (!match) return;
      var wanted = decodeURIComponent(match[1]);
      var found = null;
      this.tabs.forEach(function (tab) {
        if (tab.dataset.etBbTab === wanted) found = tab;
      });
      if (found) this.activateTab(found, false);
    }

    activateTab(tab, updateUrl) {
      this.tabs.forEach(function (other) {
        var active = other === tab;
        other.classList.toggle('et-bundle-builder__tab--active', active);
        if (active) other.setAttribute('aria-current', 'true');
        else other.removeAttribute('aria-current');
      });

      if (updateUrl && window.history && typeof window.history.replaceState === 'function') {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('bundle_category', tab.dataset.etBbTab || '');
          window.history.replaceState({}, '', url.toString());
          // The address just changed, so the hrefs built from it are one parameter stale.
          this.syncTabLinks();
        } catch (error) {
          /* A browser without URL support simply keeps the current address. */
        }
      }

      this.applyFilter();
    }

    activeTabId() {
      var active = null;
      this.tabs.forEach(function (tab) {
        if (tab.classList.contains('et-bundle-builder__tab--active')) active = tab.dataset.etBbTab;
      });
      return active;
    }

    applyFilter() {
      var activeId = this.activeTabId();
      var visible = 0;

      this.cards.forEach(function (card) {
        var tabs = ' ' + (card.dataset.etBbTabs || '') + ' ';
        var show = !activeId || tabs.indexOf(' ' + activeId + ' ') !== -1;
        card.hidden = !show;
        if (show) visible += 1;
      });

      this.updateCount(visible);
      this.applySort();
      this.updateScrollbar();
    }

    onSort() {
      this.sortTouched = true;
      if (this.sortValue && this.sortSelect) {
        var option = this.sortSelect.options[this.sortSelect.selectedIndex];
        setText(this.sortValue, option ? option.textContent.trim() : '');
      }
      this.applySort();
      if (this.grid) this.grid.scrollLeft = 0;
      this.updateScrollbar();
    }

    applySort() {
      if (!this.grid) return;
      var mode = this.sortSelect ? this.sortSelect.value : 'newest';

      // The grid is rendered in the collection's own order and the select opens on its default value, so sorting on
      // the first pass would visibly reshuffle the cards on load without anyone asking for it. Note that the sort
      // only ever reorders the cards Liquid rendered (section setting "Products to show"), never the collection.
      if (!this.sortTouched && mode === 'newest') return;

      var sorted = this.cards.slice().sort(function (a, b) {
        if (mode === 'price-ascending' || mode === 'price-descending') {
          var priceA = toInt(a.dataset.etBbPrice, 0);
          var priceB = toInt(b.dataset.etBbPrice, 0);
          if (priceA !== priceB) return mode === 'price-ascending' ? priceA - priceB : priceB - priceA;
        } else {
          var dateA = toInt(a.dataset.etBbDate, 0);
          var dateB = toInt(b.dataset.etBbDate, 0);
          if (dateA !== dateB) return dateB - dateA;
        }
        return toInt(a.dataset.etBbOrder, 0) - toInt(b.dataset.etBbOrder, 0);
      });

      var fragment = document.createDocumentFragment();
      sorted.forEach(function (card) {
        fragment.appendChild(card);
      });
      this.grid.appendChild(fragment);
    }

    /*
     * The panel's item count already carries a singular twin (data-items-label-one); the product count mirrors it, so
     * a one-product tab reads "1 product" instead of "1 products". The singular falls back to the plural, which is
     * what a language without a separate singular form wants.
     */
    updateCount(visible) {
      if (!this.countEl) return;
      var label = (visible === 1 ? this.dataset.countLabelOne || this.dataset.countLabel : this.dataset.countLabel) || '';
      setText(this.countEl, label ? visible + ' ' + label : String(visible));
    }

    /* ---------------------------------------------------------------- bundle state */

    onClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var tab = target.closest('[data-et-bb-tab]');
      if (tab && this.contains(tab)) {
        // The tabs are real links (progressive enhancement), so cmd/ctrl/shift/alt-click and the middle button must
        // keep opening the filtered URL in a new tab or window instead of being swallowed here.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.activateTab(tab, true);
        return;
      }

      var toggle = target.closest('[data-et-bb-toggle]');
      if (toggle && this.contains(toggle)) {
        event.preventDefault();
        this.toggleCard(toggle.closest('[data-et-bb-card]'));
        return;
      }

      var step = target.closest('[data-et-bb-step]');
      if (step && this.contains(step)) {
        event.preventDefault();
        var row = step.closest('[data-et-bb-item]');
        if (row) this.step(row.dataset.productId, toInt(step.dataset.etBbStep, 0));
        return;
      }

      var submit = target.closest('[data-et-bb-submit]');
      if (submit && this.contains(submit)) {
        event.preventDefault();
        this.submit();
      }
    }

    toggleCard(card) {
      if (!card || card.dataset.etBbAvailable === 'false') return;
      var id = card.dataset.etBbProduct;
      if (!id) return;

      if (this.items[id]) this.remove(id);
      else this.add(card);
    }

    add(card) {
      var id = card.dataset.etBbProduct;
      this.items[id] = {
        id: id,
        variantId: card.dataset.etBbVariant,
        quantity: 1,
        price: toInt(card.dataset.etBbPrice, 0),
        compare: toInt(card.dataset.etBbCompare, 0),
        title: card.dataset.etBbTitle || '',
        variantTitle: card.dataset.etBbVariantTitle || ''
      };
      if (this.order.indexOf(id) === -1) this.order.push(id);
      this.render();
    }

    remove(id) {
      // Pressing minus at quantity 1 deletes the row the button lives in, so focus would fall to <body>: move it to
      // the minus button of the neighbouring line, or to the panel button when the bundle is now empty.
      var entry = this.rows[id];
      var refocus = Boolean(entry && entry.row.contains(document.activeElement));
      var index = this.order.indexOf(id);

      delete this.items[id];
      if (index !== -1) this.order.splice(index, 1);
      this.render();

      if (!refocus) return;
      var neighbourId = this.order[index] || this.order[index - 1];
      var neighbour = neighbourId && this.rows[neighbourId] ? this.rows[neighbourId].row : null;
      var target = neighbour ? neighbour.querySelector('[data-et-bb-step="-1"]') : this.emptyFocusTarget();
      if (target && typeof target.focus === 'function' && !target.disabled) target.focus();
    }

    /*
     * Removing the LAST line leaves no neighbouring stepper to fall back to, and the submit button is disabled at
     * exactly that moment (an empty bundle cannot be ordered, and a disabled button cannot take focus), so focus fell
     * to <body>: a keyboard shopper was dumped at the top of the document and had to tab all the way back.
     * Focus lands on the empty-state line instead - the thing that just replaced the list, and the sentence that
     * explains what happened - with the panel heading behind it for a merchant who blanked the empty text.
     * Only a laid-out element is offered: below 750px the panel's own heading is display:none and the detached mobile
     * copy is the visible one, and focusing a hidden node would drop straight back to <body>.
     */
    emptyFocusTarget() {
      var candidates = [this.emptyEl].concat(this.panelTitles || []);
      for (var i = 0; i < candidates.length; i += 1) {
        var element = candidates[i];
        if (element && !element.hidden && element.getClientRects().length) return element;
      }
      return null;
    }

    step(id, delta) {
      var item = this.items[id];
      if (!item || !delta) return;
      var next = item.quantity + delta;
      // The Figma draws the minus enabled at quantity 1 and has no separate remove control: minus at 1 removes the line.
      if (next < 1) {
        this.remove(id);
        return;
      }
      item.quantity = Math.min(next, this.maxQuantity);
      this.render();
    }

    /* ---------------------------------------------------------------- totals */

    totals() {
      var self = this;
      var subtotal = 0;
      var compareTotal = 0;
      var count = 0;

      this.order.forEach(function (id) {
        var item = self.items[id];
        if (!item) return;
        subtotal += item.price * item.quantity;
        compareTotal += (item.compare > item.price ? item.compare : item.price) * item.quantity;
        count += item.quantity;
      });

      // The Function pools EVERY _bundle_page line in the cart into the tier, so bundle products ordered earlier count
      // towards this one's tier too (a new EUR 149,90 on top of EUR 199,90 already in the cart is 14%, not 5%). When
      // this bundle is itself the one in the cart, the cart total already includes it.
      var pooled = this.cartBundleSubtotal();
      var tierBase = this.isOrdered() ? Math.max(subtotal, pooled) : subtotal + pooled;

      var reached = null;
      var next = null;
      this.tiers.forEach(function (tier) {
        if (tierBase >= tier.minimum) reached = tier;
        else if (!next) next = tier;
      });

      // The Finex Discount App only discounts a bundle of at least this many products (quantities count), exactly
      // like the previous theme's bundle page: below it nothing is promised and the order button stays disabled.
      var qualifies = count >= this.minItems;
      if (!qualifies) reached = null;

      // Per unit, rounded down, times the quantity: what the discount Function gives in the cart (EUR 99,95 at 5% is
      // EUR 4,99, and two of them at 10% are 2 x 9,99). Each line is priced like the drawer draws it: the higher of
      // the compare-at and the price struck through, the discounted line total next to it.
      var discount = 0;
      var lines = {};
      this.order.forEach(function (id) {
        var item = self.items[id];
        if (!item) return;
        var off = reached ? Math.floor((item.price * reached.percentage) / 100) * item.quantity : 0;
        discount += off;
        lines[id] = {
          compare: Math.max(item.compare, item.price) * item.quantity,
          final: item.price * item.quantity - off,
          // Unknown until Shopify answers: a row keeps the titles it already shows instead of flickering empty.
          discounts: null
        };
      });

      return {
        count: count,
        qualifies: qualifies,
        subtotal: subtotal,
        tierBase: tierBase,
        compareTotal: compareTotal,
        discount: discount,
        total: subtotal - discount,
        // The instant ESTIMATE from the tier blocks. pricedTotals() replaces lines, total, discount and savings with
        // Shopify's own numbers as soon as they arrive; tierBase, reached and next stay, they drive the threshold bar.
        savings: compareTotal - (subtotal - discount),
        lines: lines,
        presentment: false,
        reached: reached,
        next: next
      };
    }

    /* ---------------------------------------------------------------- rendering */

    render() {
      this.renderCards();
      this.renderPrices();

      this.updateSubmitState();
      if (this.emptyEl) this.emptyEl.hidden = this.order.length > 0;
      this.hideError();
      this.checkSticky();
    }

    /** Everything that shows money. Also the entry point when Shopify's prices land or the cart changes. */
    renderPrices() {
      var totals = this.pricedTotals(this.totals());
      this.renderItems(totals);
      this.renderTotals(totals);
      this.renderThreshold(totals);
      this.schedulePricing();
      this.updatePricingState();
    }

    renderCards() {
      var self = this;
      var addLabel = this.dataset.addLabel || 'Add to Bundle';
      var removeLabel = this.dataset.removeLabel || 'Remove';

      this.cards.forEach(function (card) {
        var button = card.querySelector('[data-et-bb-toggle]');
        if (!button || card.dataset.etBbAvailable === 'false') return;
        var inBundle = Boolean(self.items[card.dataset.etBbProduct]);
        button.setAttribute('aria-pressed', inBundle ? 'true' : 'false');
        card.classList.toggle('et-bundle-builder__card--in-bundle', inBundle);
        setText(card.querySelector('[data-et-bb-toggle-label]'), inBundle ? removeLabel : addLabel);
      });
    }

    /*
     * The rows are updated IN PLACE and only added or removed when a line joins or leaves the bundle. Wiping the
     * container on every render would take the +/- button the shopper just pressed out of the document, dropping
     * focus to <body> - one keyboard press per line, then a full tab back through the page.
     * this.rows maps a product id to its row and to the 1px rule that follows it. Figma 54565:104047 draws a rule
     * above the first line, between the lines and below the last one, each with the 16px gap on both sides, so the
     * rules are elements, never border-top: one leading rule plus one after every row.
     */
    renderItems(totals) {
      if (!this.itemsEl || !this.itemTemplate) return;
      var self = this;
      var order = this.order;

      Object.keys(this.rows).forEach(function (id) {
        if (order.indexOf(id) !== -1) return;
        var entry = self.rows[id];
        if (entry.row.parentNode) entry.row.parentNode.removeChild(entry.row);
        if (entry.divider.parentNode) entry.divider.parentNode.removeChild(entry.divider);
        delete self.rows[id];
      });

      // An empty in-flow box would still take the panel's 16px gap on both sides of its zero height
      this.itemsEl.hidden = !order.length;

      if (!order.length) {
        this.itemsEl.textContent = '';
        this.leadDivider = null;
        return;
      }

      if (!this.leadDivider || this.leadDivider.parentNode !== this.itemsEl) {
        this.leadDivider = this.divider();
        this.itemsEl.insertBefore(this.leadDivider, this.itemsEl.firstChild);
      }

      // A product is always appended to the end of this.order, so appending here keeps the two in step.
      order.forEach(function (id) {
        var item = self.items[id];
        if (!item) return;

        var entry = self.rows[id];
        if (!entry) {
          entry = { row: self.itemTemplate.content.firstElementChild.cloneNode(true), divider: self.divider() };
          entry.row.dataset.productId = id;
          self.rows[id] = entry;
          self.itemsEl.appendChild(entry.row);
          self.itemsEl.appendChild(entry.divider);
        }

        self.updateItem(entry.row, item, totals.lines[id], totals.presentment);
      });
    }

    updateItem(row, item, line, presentment) {
      setText(row.querySelector('[data-et-bb-item-title]'), item.title);

      var variant = row.querySelector('[data-et-bb-item-variant]');
      if (variant) {
        variant.hidden = !item.variantTitle;
        setText(variant, item.variantTitle);
      }

      // Exactly the drawer's price pair (snippets/et-cart-drawer-line-item.liquid): the struck amount only when it is
      // higher than what the line really costs, which includes every discount on it.
      if (!line) line = { compare: item.compare * item.quantity, final: item.price * item.quantity, discounts: null };
      var compare = row.querySelector('[data-et-bb-item-compare]');
      var price = row.querySelector('[data-et-bb-item-price]');
      var onSale = line.compare > line.final;
      if (compare) {
        compare.hidden = !onSale;
        setText(compare, onSale ? formatMoney(line.compare, false, presentment) : '');
      }
      if (price) {
        price.classList.toggle('et-bundle-builder__item-price--sale', onSale);
        setText(price, formatMoney(line.final, false, presentment));
      }
      if (line.discounts) this.renderDiscounts(row, line.discounts);

      setText(row.querySelector('[data-et-bb-qty]'), String(item.quantity));

      // The labels are [title] templates from the Liquid (locales/*.json carries "Remove {{ title }}"), never a
      // string built here: a language with another word order is fixed in the template, not in JavaScript.
      var minus = row.querySelector('[data-et-bb-step="-1"]');
      if (minus) {
        var minusLabel = item.quantity === 1 ? minus.dataset.labelRemove : minus.dataset.labelDecrease;
        if (minusLabel) minus.setAttribute('aria-label', fill(minusLabel, '[title]', item.title));
      }

      var plus = row.querySelector('[data-et-bb-step="1"]');
      if (plus) {
        plus.disabled = item.quantity >= this.maxQuantity;
        var plusLabel = plus.dataset.labelIncrease;
        if (plusLabel) plus.setAttribute('aria-label', fill(plusLabel, '[title]', item.title));
      }
    }

    /** The discount titles of a line - the drawer's chips ("Bundelvoordeel 10%", "Free Smart Plug", a code). */
    renderDiscounts(row, titles) {
      var list = row.querySelector('[data-et-bb-item-discounts]');
      if (!list) return;
      var signature = titles.join('\n');
      if (list.dataset.titles === signature) return;
      list.dataset.titles = signature;
      list.textContent = '';
      titles.forEach(function (title) {
        var chip = document.createElement('li');
        chip.className = 'et-bundle-builder__item-discount';
        chip.textContent = title;
        list.appendChild(chip);
      });
      list.hidden = !titles.length;
    }

    divider() {
      var rule = document.createElement('div');
      rule.className = 'et-bundle-builder__item-divider';
      return rule;
    }

    renderTotals(totals) {
      var selectedTemplate =
        totals.count === 1
          ? this.dataset.itemsLabelOne || this.dataset.itemsLabel || ''
          : this.dataset.itemsLabel || '';

      var selectedText = fill(selectedTemplate, '[count]', String(totals.count));
      this.selectedEls.forEach(function (element) {
        setText(element, selectedText);
      });

      setText(this.totalEl, formatMoney(totals.total, false, totals.presentment));

      if (this.savingsEl) {
        var show = totals.savings > 0;
        var prefix = this.dataset.saveLabel || '';
        this.savingsEl.hidden = !show;
        // Emptied when it hides: the badge sits inside the aria-live total row, so a stale "Save EUR 50,00" left in a
        // hidden node is one CSS rule (and one screen-reader mode) away from the shopper, and the next time the badge
        // is shown it would paint the previous amount for a frame before the new one.
        setText(this.savingsEl, show ? (prefix ? prefix + ' ' : '') + formatMoney(totals.savings, false, totals.presentment) : '');
      }
    }

    renderThreshold(totals) {
      if (!this.thresholdEl) return;

      if (!this.tiers.length) {
        this.hideThreshold();
        return;
      }

      // An empty bundle has no progress to report: "Spend EUR 150 more to unlock 5% savings" at a 0% bar promises a
      // discount on a bundle that does not exist yet, and the bar reads as a ladder the shopper has already stepped
      // onto. This is also exactly the state the Liquid renders (the box ships hidden), so nothing shifts on load.
      if (!totals.count) {
        this.hideThreshold();
        return;
      }

      var template = '';
      var amountText = '';
      var percentText = '';
      var progress = 0;

      if (!totals.qualifies && this.dataset.thresholdMinLabel) {
        template = fill(this.dataset.thresholdMinLabel, '[min]', String(this.minItems));
        template = fill(template, '[count]', String(Math.max(0, this.minItems - totals.count)));
      } else if (totals.next) {
        var remaining = Math.max(0, totals.next.minimum - totals.tierBase);
        // "Spend EUR 39 more" is written without decimals in the design, so the remainder is rounded up to a whole
        // unit: the amount shown always reaches the tier.
        var rounded = Math.ceil(remaining / 100) * 100;
        template = totals.next.label || this.dataset.thresholdLabel || '';
        amountText = formatMoney(rounded, true);
        percentText = totals.next.percentage + '%';
      } else if (totals.reached) {
        template = this.dataset.thresholdReachedLabel || '';
        amountText = formatMoney(totals.discount, false, totals.presentment);
        percentText = totals.reached.percentage + '%';
      }

      if (!template) {
        this.hideThreshold();
        return;
      }

      progress = this.progressPercent(totals.tierBase);

      // The fill's width transition (CSS) needs a width to move FROM. A box that is still display:none has none, so
      // on the first add (Liquid ships the box hidden, and hideThreshold hides it again once the bundle is emptied)
      // the bar would simply appear at its final width. Showing the box at 0% and forcing one layout before the real
      // value lands gives the transition its start point: the first item grows the bar in from empty, exactly like
      // every item after it moves it forward.
      if (this.thresholdEl.hidden) {
        this.thresholdEl.style.setProperty('--et-bb-progress', '0');
        this.thresholdEl.hidden = false;
        void this.thresholdEl.offsetWidth;
      }

      this.thresholdEl.style.setProperty('--et-bb-progress', String(Math.max(0, Math.min(100, Math.round(progress)))));
      this.writeThresholdMessage(template, amountText, percentText);
    }

    /*
     * The Figma (54565:104095) draws ONE continuous run - a single fill, a single knob, a single remainder, with no
     * tier ticks anywhere on the track - so the bar measures the whole savings ladder and 100% is the TOP tier.
     * It used to measure the subtotal against the NEXT tier's minimum, which made the fill jump BACKWARDS the instant
     * a tier was crossed: on the live tiers (150 / 250 / 400) EUR 149,99 drew the bar at 100% and EUR 150,01 drew it
     * at 60%, so spending more shrank the bar right at the moment the shopper had just been rewarded. Measuring
     * INSIDE the current tier is worse still (100% -> 0%). Against the top tier the fill is a monotonic function of
     * the subtotal: adding to the bundle can only ever move the knob forward.
     * The sentence above the bar still names the next tier, and over the last stretch - the one the shopper is
     * actually deciding in - the two models agree exactly, because there the next tier IS the top tier.
     */
    progressPercent(subtotal) {
      var top = this.tiers.length ? this.tiers[this.tiers.length - 1].minimum : 0;
      if (top <= 0) return 0;
      return Math.min(100, (subtotal / top) * 100);
    }

    /** Hidden AND emptied: a stale sentence and a stale fill would flash before the next render paints over them. */
    hideThreshold() {
      if (!this.thresholdEl) return;
      this.thresholdEl.hidden = true;
      this.thresholdEl.style.setProperty('--et-bb-progress', '0');
      setText(this.thresholdMessage, '');
    }

    /*
     * Figma 54565:104094: one sentence with two runs in the medium weight - the amount, and "7% savings", which is
     * also coral. The coral run is TWO WORDS, so a [percent] token alone cannot describe it: the copy setting marks
     * the run with [hi] ... [/hi] and everything between them is drawn in the accent style. Together with the
     * [amount] and [percent] value tokens the whole sentence stays translatable, in any word order.
     */
    writeThresholdMessage(template, amountText, percentText) {
      var element = this.thresholdMessage;
      if (!element) return;

      element.textContent = '';
      var target = element;

      template.split(/(\[amount\]|\[percent\]|\[hi\]|\[\/hi\])/).forEach(function (part) {
        if (!part) return;

        if (part === '[hi]') {
          if (target !== element) return;
          target = document.createElement('strong');
          target.className = 'et-bundle-builder__threshold-percent';
          element.appendChild(target);
          return;
        }

        if (part === '[/hi]') {
          target = element;
          return;
        }

        if (part === '[amount]' || part === '[percent]') {
          var value = part === '[amount]' ? amountText : percentText;

          // Inside the highlighted run the medium weight is already there; a nested <strong> would add nothing.
          if (target !== element) {
            target.appendChild(document.createTextNode(value));
            return;
          }

          var strong = document.createElement('strong');
          strong.className =
            part === '[amount]'
              ? 'et-bundle-builder__threshold-amount'
              : 'et-bundle-builder__threshold-percent';
          strong.textContent = value;
          element.appendChild(strong);
          return;
        }

        target.appendChild(document.createTextNode(part));
      });
    }

    /* ---------------------------------------------------------------- mobile scroller pill */

    updateScrollbar() {
      if (!this.grid || !this.scrollbar || !this.scrollbarThumb) return;

      if (!matches(MOBILE_QUERY)) {
        this.scrollbar.hidden = false;
        return;
      }

      var track = this.scrollbarThumb.parentElement;
      var trackWidth = track ? track.clientWidth : 0;
      var scrollable = this.grid.scrollWidth - this.grid.clientWidth;

      if (scrollable <= 1 || !trackWidth) {
        this.scrollbar.hidden = true;
        return;
      }

      this.scrollbar.hidden = false;
      var ratio = this.grid.clientWidth / this.grid.scrollWidth;
      var thumbWidth = Math.max(16, Math.round(trackWidth * ratio));
      var offset = Math.round((trackWidth - thumbWidth) * (this.grid.scrollLeft / scrollable));

      this.scrollbarThumb.style.width = thumbWidth + 'px';
      this.scrollbarThumb.style.transform = 'translateX(' + offset + 'px)';
    }

    onResizeHandler() {
      this.updateScrollbar();
      this.checkSticky();
    }

    /* ---------------------------------------------------------------- sticky panel (desktop) */

    initSticky() {
      if (!this.panel || this.dataset.stickyPanel !== 'true') return;
      if (typeof window.ResizeObserver === 'function') {
        this.stickyObserver = new window.ResizeObserver(this.checkSticky.bind(this));
        this.stickyObserver.observe(this.panel);
      }
      this.checkSticky();
    }

    /** Sticky only while the whole panel fits under the sticky header - the guard in assets/et-cart-page.js. */
    checkSticky() {
      if (!this.panel || this.dataset.stickyPanel !== 'true') return;
      if (!matches(TWO_COLUMN_QUERY)) {
        this.panel.classList.remove('et-bundle-builder__panel--sticky');
        return;
      }
      var top = parseFloat(window.getComputedStyle(this.panel).top) || 0;
      var fits = this.panel.offsetHeight > 0 && this.panel.offsetHeight <= window.innerHeight - top;
      this.panel.classList.toggle('et-bundle-builder__panel--sticky', fits);
    }

    /* ---------------------------------------------------------------- Shopify prices */

    /** The estimate, with Shopify's numbers laid over it once they are known for exactly this bundle and cart. */
    pricedTotals(totals) {
      var entry = this.order.length && this.cart ? this.pricing[this.pricingKey()] : null;
      if (!entry || !entry.lines) return totals;
      return Object.assign({}, totals, {
        lines: entry.lines,
        total: entry.total,
        discount: entry.discount,
        savings: entry.compareTotal - entry.total,
        presentment: true
      });
    }

    /*
     * The bundle is already in the cart (just ordered, unchanged): the CTA stays locked and the cart is priced as it
     * is, never with the bundle twice. Only while the cart still holds it - removed in the drawer, it can be ordered
     * again (before, the button stayed disabled for good).
     */
    isOrdered() {
      return this.order.length > 0 && this.bundleSignature() === this.orderedSignature && this.bundleInCart();
    }

    /** Pre-discount value of the cart lines that carry the bundle property (what the Function tiers on). */
    cartBundleSubtotal() {
      var cart = this.cart;
      var key = this.dataset.propertyKey;
      if (!cart || !key) return 0;
      var value = this.dataset.propertyValue || '';
      var total = 0;
      (cart.items || []).forEach(function (line) {
        if (line.properties && String(line.properties[key]) === value) total += line.original_line_price || 0;
      });
      return total;
    }

    bundleInCart() {
      var cart = this.cart;
      if (!cart) return true;
      var self = this;
      var wanted = toAttributes(this.properties());
      return this.order.every(function (id) {
        var item = self.items[id];
        if (!item) return true;
        var quantity = 0;
        (cart.items || []).forEach(function (line) {
          if (String(line.variant_id) === String(item.variantId) && hasAttributes({ attributes: toAttributes(line.properties) }, wanted)) {
            quantity += line.quantity;
          }
        });
        return quantity >= item.quantity;
      });
    }

    pricingKey() {
      return (this.isOrdered() ? 'in-cart' : 'add') + '#' + this.bundleSignature() + '#' + this.cartSignature();
    }

    cartSignature() {
      var cart = this.cart;
      if (!cart) return '';
      var items = (cart.items || []).map(function (item) {
        var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
        return item.variant_id + 'x' + item.quantity + (plan ? '@' + plan.id : '') + JSON.stringify(item.properties || {});
      });
      return items.join('|') + '#' + this.discountCodes(cart).join(',') + '#' + JSON.stringify(cart.attributes || {});
    }

    discountCodes(cart) {
      return ((cart && cart.discount_codes) || [])
        .map(function (code) {
          return code && code.code;
        })
        .filter(Boolean);
    }

    loadCart() {
      var self = this;
      if (this.cart) return Promise.resolve(this.cart);
      if (!this.cartRequest) {
        this.cartRequest = fetch(routesRoot + 'cart.js', {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('cart.js ' + response.status);
            return response.json();
          })
          .catch(function () {
            // Unreadable cart: price the bundle on its own rather than not at all.
            return { items: [], discount_codes: [], attributes: {} };
          })
          .then(function (cart) {
            self.cart = cart;
            self.cartRequest = null;
            return cart;
          });
      }
      return this.cartRequest;
    }

    onCartChangeHandler(event) {
      var cart = event && event.detail && event.detail.cart;
      this.cart = cart && Array.isArray(cart.items) ? cart : null;
      if (!this.cart) this.cartRequest = null;
      if (this.ready && this.order.length) {
        this.renderPrices();
        this.updateSubmitState();
      }
    }

    schedulePricing() {
      window.clearTimeout(this.pricingTimer);
      if (!this.order.length || this.busy) return;
      if (this.cart && this.pricing[this.pricingKey()]) return;

      var self = this;
      this.pricingTimer = window.setTimeout(function () {
        self.loadCart().then(function () {
          if (!self.order.length || self.busy) return;
          var key = self.pricingKey();
          if (self.pricing[key]) {
            self.renderPrices();
            return;
          }
          self.pricing[key] = { pending: true };
          self
            .fetchPricing(self.pricingInput())
            .catch(function () {
              return null;
            })
            .then(function (result) {
              self.pricing[key] = result || { failed: true };
              if (self.pricingKey() === key) self.renderPrices();
            });
        });
      }, PRICING_DELAY);
    }

    /** Snapshot of what is priced: the cart lines, plus the bundle lines unless they are already in it. */
    pricingInput() {
      var self = this;
      var cart = this.cart || {};
      var properties = toAttributes(this.properties());
      var lines = [];

      (cart.items || []).forEach(function (item) {
        var line = {
          merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
          quantity: item.quantity,
          attributes: toAttributes(item.properties)
        };
        var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
        if (plan && plan.id) line.sellingPlanId = 'gid://shopify/SellingPlan/' + plan.id;
        lines.push(line);
      });

      var bundle = [];
      this.order.forEach(function (id) {
        var item = self.items[id];
        if (!item || !item.variantId) return;
        bundle.push({ productId: id, variantId: String(item.variantId), quantity: item.quantity });
      });

      if (!this.isOrdered()) {
        bundle.forEach(function (entry) {
          lines.push({
            merchandiseId: 'gid://shopify/ProductVariant/' + entry.variantId,
            quantity: entry.quantity,
            attributes: properties
          });
        });
      }

      var input = { lines: lines };
      var codes = this.discountCodes(cart);
      if (codes.length) input.discountCodes = codes;
      var attributes = toAttributes(cart.attributes);
      if (attributes.length) input.attributes = attributes;
      var country = window.Shopify && window.Shopify.country;
      if (country) input.buyerIdentity = { countryCode: country };

      return { input: input, country: country, bundle: bundle, properties: properties };
    }

    fetchPricing(snapshot) {
      var self = this;
      var query = snapshot.country
        ? 'mutation EtBundlePrice($input: CartInput!, $country: CountryCode!) @inContext(country: $country) { '
        : 'mutation EtBundlePrice($input: CartInput!) { ';
      var variables = { input: snapshot.input };
      if (snapshot.country) variables.country = snapshot.country;

      /*
       * Cross-origin, on the permanent myshopify.com domain, with no credentials - never the storefront's own /api path.
       * The Storefront API answers with a fresh _shopify_essential session cookie: on the same origin that cookie
       * replaced the shopper's session, and the drawer then rendered for a new, EMPTY cart right after the bundle was
       * added (reproduced on the preview). A credential-less cross-origin response cannot set a cookie at all, and the
       * API allows any origin (Access-Control-Allow-Origin: *).
       */
      var shop = window.Shopify && window.Shopify.shop;
      var endpoint = (shop ? 'https://' + shop : '') + '/api/' + STOREFRONT_API_VERSION + '/graphql.json';

      return fetch(endpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query: query + PRICING_SELECTION + ' }', variables: variables })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Storefront API ' + response.status);
          return response.json();
        })
        .then(function (data) {
          return self.readPricing(data, snapshot);
        });
    }

    /*
     * Shopify may split a line (a "3 + 1 free" leaves 3 paid units on one line and 1 free unit on another) and merges
     * a bundle line with the same product + property already in the cart, so the lines are grouped per variant and
     * the bundle takes its share of each group. Cart-level (order) discounts are shared out by line value; with only
     * the bundle in the cart that is the whole discount, exactly the drawer's total.
     */
    readPricing(data, snapshot) {
      var cart = data && data.data && data.data.cartCreate && data.data.cartCreate.cart;
      if (!cart || !cart.lines) return null;

      var groups = {};
      var allTotal = 0;
      cart.lines.nodes.forEach(function (line) {
        var total = cents(line.cost && line.cost.totalAmount);
        allTotal += total;
        var variantId = String((line.merchandise && line.merchandise.id) || '').split('/').pop();
        if (!hasAttributes(line, snapshot.properties)) return;

        var group = groups[variantId] || (groups[variantId] = { quantity: 0, total: 0, price: 0, compare: 0, titles: [] });
        var unit = cents(line.cost && line.cost.amountPerQuantity);
        var compareUnit = cents(line.cost && line.cost.compareAtAmountPerQuantity);
        group.quantity += line.quantity;
        group.total += total;
        group.price += unit * line.quantity;
        group.compare += Math.max(unit, compareUnit) * line.quantity;
        (line.discountAllocations || []).forEach(function (allocation) {
          var title = allocation.title || allocation.code;
          if (title && group.titles.indexOf(title) === -1) group.titles.push(title);
        });
      });

      var lines = {};
      var total = 0;
      var price = 0;
      var compareTotal = 0;
      var complete = snapshot.bundle.every(function (entry) {
        var group = groups[entry.variantId];
        // Missing or fewer units than the bundle (stock capped it, or the drawer changed an ordered bundle): the
        // numbers would describe something else, so the estimate stays.
        if (!group || group.quantity < entry.quantity) return false;
        var share = entry.quantity / group.quantity;
        var line = {
          compare: Math.round(group.compare * share),
          final: Math.round(group.total * share),
          discounts: group.titles
        };
        lines[entry.productId] = line;
        total += line.final;
        compareTotal += line.compare;
        price += Math.round(group.price * share);
        return true;
      });
      if (!complete) return null;

      var orderDiscount = 0;
      (cart.discountAllocations || []).forEach(function (allocation) {
        orderDiscount += cents(allocation.discountedAmount);
      });
      if (orderDiscount > 0 && allTotal > 0) total -= Math.round((orderDiscount * total) / allTotal);

      return { lines: lines, total: total, compareTotal: compareTotal, discount: price - total };
    }

    /* Dims the prices while Shopify's answer for the current bundle is outstanding (CSS delays it, so a quick answer
       never flickers), and holds the total's live region so a screen reader announces the final amount only. */
    updatePricingState() {
      var entry = this.order.length && this.cart ? this.pricing[this.pricingKey()] : null;
      var pending = this.order.length > 0 && !this.busy && (!entry || entry.pending === true);
      this.classList.toggle('et-bundle-builder__root--pricing', pending);
      var live = this.totalEl && this.totalEl.closest('[aria-live]');
      if (live) live.setAttribute('aria-busy', pending ? 'true' : 'false');
    }

    /* ---------------------------------------------------------------- cart */

    /*
     * Every bundle line carries the hidden property "_bundle_page: true" (section settings, same defaults). The
     * automatic discount "Bundelkorting (bundelpagina)" from the custom Finex Discount App (Function
     * finex-bundle-discount) applies its tier percentage to those lines only, so the same product added from a product
     * page stays full price - the mechanism the previous theme's bundle page uses. A wrong key means the discount
     * silently never applies. The optional second property (a shared bundle id) is not read by the Function.
     * A property whose key starts with "_" is already hidden in both cart surfaces, no extra work needed.
     */
    properties() {
      var properties = {};
      var key = this.dataset.propertyKey;
      if (key) properties[key] = this.dataset.propertyValue || '';

      var bundleKey = this.dataset.bundleIdKey;
      if (bundleKey) {
        if (!this.bundleId) {
          this.bundleId = 'bb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
        }
        properties[bundleKey] = this.bundleId;
      }

      return properties;
    }

    cartItems() {
      var self = this;
      var properties = this.properties();
      var items = [];

      this.order.forEach(function (id) {
        var item = self.items[id];
        if (!item || !item.variantId) return;
        items.push({
          id: toInt(item.variantId, 0),
          quantity: item.quantity,
          properties: properties
        });
      });

      return items;
    }

    /*
     * ONE bundle per click, always.
     * `this.busy` was already here and it does guard the in-flight window, but that is the only window it ever
     * covered: `setBusy(false)` in the success handler re-armed the button while the panel still showed the very same
     * bundle, so the next click POSTed an identical items[] and the shopper silently paid for two bundles - a real
     * double-click that lands just after a fast add is enough. Two things now stand in the way:
     *   1. the button is disabled for the whole request (`busy`), so a native second click is never dispatched, and
     *   2. it STAYS disabled while the panel still shows exactly the bundle that was ordered (`orderedSignature`).
     * Changing anything - a product, a quantity - re-arms it, and a FAILED add re-arms it immediately so the shopper
     * can retry. The guard is repeated here, not left to the disabled attribute alone, because a synthetic click
     * still bubbles out of a disabled button.
     */
    submit() {
      if (this.busy) return;
      if (!this.totals().qualifies) return;
      var items = this.cartItems();
      if (!items.length) return;

      // Captured BEFORE the request: the steppers stay live while it is in flight, so this must describe what was
      // actually posted, not what the panel happens to show when the response lands.
      var signature = this.bundleSignature();
      if (signature === this.orderedSignature) return;

      var self = this;
      this.setBusy(true);
      this.hideError();

      var request =
        window.EtCartPage && typeof window.EtCartPage.add === 'function'
          ? window.EtCartPage.add(items)
          : this.postItems(items);

      request
        .then(function () {
          self.orderedSignature = signature;
          self.setBusy(false);
          self.renderPrices();
          self.openCart();
        })
        .catch(function (error) {
          self.setBusy(false);
          self.showError(error);
          self.renderPrices();
        });
    }

    /** One request for the whole bundle: cart/add.js takes as many items[] as needed and fails atomically. */
    postItems(items) {
      var sections = this.bundledSectionIds();
      var body = { items: items };

      if (sections.length) {
        body.sections = sections.slice(0, SECTIONS_PER_REQUEST);
        body.sections_url = window.location.pathname;
      }

      return fetch(routesRoot + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          // Required by Shopify for the inventory check (see assets/theme.js).
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              var error = new Error((data && (data.description || data.message)) || 'cart/add.js failed');
              error.data = data;
              throw error;
            }
            return data;
          });
        })
        .then(function (data) {
          var rendered = (data && data.sections) || {};
          return fetch(routesRoot + 'cart.js', {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          })
            .then(function (response) {
              return response.json();
            })
            .then(function (cart) {
              var ready =
                sections.length &&
                sections.every(function (id) {
                  return typeof rendered[id] === 'string';
                });

              if (ready) {
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: { baseEvent: 'variant:add', cart: Object.assign({}, cart, { sections: rendered }) }
                  })
                );
              } else {
                // A missing section would throw inside the drawer: let the theme components re-fetch themselves.
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              }

              return cart;
            });
        });
    }

    /** Never hardcode the bundled section ids: every listener pushes its own (assets/theme.js CartDrawer). */
    bundledSectionIds() {
      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
      );

      var unique = [];
      sections.forEach(function (id) {
        var value = String(id);
        if (value && unique.indexOf(value) === -1) unique.push(value);
      });
      return unique;
    }

    openCart() {
      var cartType = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType) || '';

      if (cartType === 'page') {
        window.location.href = routesRoot + 'cart';
        return;
      }

      var drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.show === 'function') {
        drawer.show();
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.submitEl) return;
      this.submitEl.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.updateSubmitState();
    }

    /** The exact bundle, as posted: same variants, same order, same quantities. */
    bundleSignature() {
      var self = this;
      return this.order
        .map(function (id) {
          var item = self.items[id];
          return item ? item.variantId + ':' + item.quantity : '';
        })
        .join('|');
    }

    /** The one place that decides whether the CTA is clickable - render and setBusy both come through here. */
    updateSubmitState() {
      if (!this.submitEl) return;
      this.submitEl.disabled = this.busy || this.order.length === 0 || this.isOrdered() || !this.totals().qualifies;
    }

    showError(error) {
      if (!this.errorEl) return;
      var message = (error && (error.message || error.description)) || '';
      if (!message) return;
      setText(this.errorEl, message);
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      setText(this.errorEl, '');
    }
  }

  window.customElements.define('et-bundle-builder', EtBundleBuilder);

  /* ------------------------------------------------------------------ theme editor */

  function buildersIn(scope) {
    if (!(scope instanceof Element) && scope !== document) return [];
    return Array.prototype.slice.call(scope.querySelectorAll('et-bundle-builder'));
  }

  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block || typeof block.closest !== 'function') return;

    var builder = block.closest('et-bundle-builder');
    var tab = block.querySelector ? block.querySelector('[data-et-bb-tab]') : null;
    if (builder && tab && typeof builder.activateTab === 'function') builder.activateTab(tab, false);
  });

  document.addEventListener('shopify:section:load', function (event) {
    buildersIn(event.target).forEach(function (builder) {
      if (typeof builder.checkSticky === 'function') builder.checkSticky();
    });
  });
})();
